// v8.9.6 — BRR Gate Trace widget.
//
// Shows per-symbol which of the 7 BRR gates is currently passing/failing
// so you can see at-a-glance that "XAG is blocked on gate 6: no new
// 20-bar high in last 12 bars" without reading code.
//
// Polls /api/bot/brr-gate-trace/{account_id} every 8s.
import { useEffect, useState } from "react";
import { Loader2, Search, CheckCircle2, XCircle, Circle } from "lucide-react";
import { api } from "@/lib/api";

const GATE_LABELS = [
  { key: "gate_1_trend", label: "Trend" },
  { key: "gate_2_micro_cross", label: "EMA12/25 (bonus)" },
  { key: "gate_3_rsi", label: "RSI(14)" },
  { key: "gate_4_adx", label: "ADX(14)" },
  { key: "gate_5_atr", label: "ATR" },
  { key: "gate_6_breakout", label: "Breakout" },
  { key: "gate_7_retest", label: "Retest" },
];

const cellTone = (val) => {
  if (val === null || val === undefined) return { Icon: Circle, tone: "text-vts-silver/25", title: "Not evaluated" };
  if (typeof val === "string" && val.startsWith("PASS")) return { Icon: CheckCircle2, tone: "text-vts-profit", title: val };
  if (typeof val === "string" && val.startsWith("FAIL")) return { Icon: XCircle, tone: "text-vts-loss", title: val };
  if (typeof val === "string" && val === "warmup") return { Icon: Loader2, tone: "text-vts-blue", title: "Waiting for warmup" };
  if (typeof val === "string" && val === "flat") return { Icon: Circle, tone: "text-amber-300", title: "Flat — no direction" };
  if (typeof val === "string" && val === "flat_no_bonus") return { Icon: Circle, tone: "text-vts-silver/55", title: "Flat — no score bonus" };
  return { Icon: Circle, tone: "text-vts-silver/40", title: String(val) };
};

export function BrrGateTraceWidget({ accountId, running }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!accountId || !running) {
      setRows([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const fetchTrace = async () => {
      try {
        const { data } = await api.get(`/bot/brr-gate-trace/${accountId}`);
        if (cancelled) return;
        if (data?.ok) setRows(data.rows || []);
        else setRows([]);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e?.message || "fetch failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTrace();
    const id = setInterval(fetchTrace, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [accountId, running]);

  if (!running) return null;
  if (loading && rows.length === 0) {
    return (
      <div data-testid="brr-gate-trace-loading" className="vts-card p-5 lg:p-6">
        <div className="flex items-center gap-2 text-vts-silver/60 text-sm font-mono">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading BRR gate trace…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="brr-gate-trace-error" className="vts-card p-5 lg:p-6 text-[11px] font-mono text-vts-loss">
        Failed to load BRR gate trace: {error}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div data-testid="brr-gate-trace-empty" className="vts-card p-5 lg:p-6 text-[11px] font-mono text-vts-silver/55">
        BRR scanner hasn’t evaluated any symbols yet. Wait ~30s after Start.
      </div>
    );
  }

  const visible = showAll ? rows : rows.slice(0, 12);
  const firedCount = rows.filter((r) => r.fired).length;

  return (
    <div data-testid="brr-gate-trace-panel" className="vts-card p-5 lg:p-6">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-heading font-semibold text-base text-white tracking-tight flex items-center gap-2">
            <Search className="h-4 w-4 text-vts-blue" />
            BRR Gate Trace · why isn’t it firing?
          </h3>
          <p className="text-[11px] text-vts-silver/55 mt-1 font-mono leading-snug max-w-xl">
            Per-symbol snapshot of the 7 BRR gates. Green = pass, red = the gate blocking the fire,
            grey = not yet evaluated (earlier gate failed first).
          </p>
        </div>
        <div className="text-[10px] font-mono text-vts-silver/55 flex items-center gap-2">
          <span>{rows.length} symbols</span>
          <span className="text-vts-silver/30">·</span>
          <span className={firedCount > 0 ? "text-vts-profit" : "text-vts-silver/55"}>
            {firedCount} fired this tick
          </span>
        </div>
      </div>

      <div className="rounded border border-vts-line overflow-x-auto">
        <div
          className="grid text-[9px] uppercase tracking-wider font-mono text-vts-silver/50 bg-vts-bg/60 px-3 py-2 border-b border-vts-line"
          style={{ gridTemplateColumns: "minmax(110px,1fr) repeat(7, 60px) 1fr" }}
        >
          <div>Symbol</div>
          {GATE_LABELS.map((g) => (
            <div key={g.key} className="text-center" title={g.label}>
              {g.label.replace(" (bonus)", "*")}
            </div>
          ))}
          <div>Detail</div>
        </div>
        <div className="divide-y divide-vts-line/50">
          {visible.map((row) => (
            <div
              key={row.symbol}
              data-testid={`brr-gate-trace-row-${row.symbol}`}
              className={`grid items-center px-3 py-1.5 text-[11px] font-mono ${
                row.fired ? "bg-vts-profit/10" : "hover:bg-vts-surface/40"
              }`}
              style={{ gridTemplateColumns: "minmax(110px,1fr) repeat(7, 60px) 1fr" }}
            >
              <div className="text-white truncate flex items-center gap-1.5">
                {row.fired && <CheckCircle2 className="h-3 w-3 text-vts-profit shrink-0" />}
                {row.symbol}
              </div>
              {GATE_LABELS.map((g) => {
                const { Icon, tone, title } = cellTone(row[g.key]);
                return (
                  <div key={g.key} className="flex justify-center" title={title}>
                    <Icon className={`h-3.5 w-3.5 ${tone} ${title === "Waiting for warmup" ? "animate-spin" : ""}`} />
                  </div>
                );
              })}
              <div className="text-vts-silver/65 truncate" title={row.summary || ""}>
                {row.summary || "—"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {rows.length > 12 && (
        <button
          type="button"
          data-testid="brr-gate-trace-show-all"
          onClick={() => setShowAll((s) => !s)}
          className="mt-2 text-[10px] font-mono uppercase tracking-wider text-vts-silver/55 hover:text-white transition"
        >
          {showAll ? "Show top 12 only" : `Show all ${rows.length}`}
        </button>
      )}

      <p className="text-[10px] text-vts-silver/40 font-mono mt-3 leading-relaxed">
        Gate 2 (EMA12/25) is a <span className="text-vts-blue">score bonus only</span> — won’t block a fire.
        Gates 1, 4, 5, 6 are hard requirements. Gate 7 (retest) is an alternate path to Gate 6.
      </p>
    </div>
  );
}

export default BrrGateTraceWidget;
